/**
 * 2D 오버레이 레이어의 **표시 대상 필터 + 페인팅 순서(아래→위)** 파생 — 순수 함수만 둔다.
 *
 * 화면 렌더(`OverlayLayers`)와 서버 캡쳐 요청(`layers` 배열, 앞이 아래)이 이 모듈의
 * 결과를 공유한다. 두 곳이 순서 규칙을 각자 구현하면 한쪽만 바뀌며 조용히 어긋나므로,
 * 순서·표시 판정의 출처는 여기 하나여야 한다.
 *
 * zIndex **값** 산출은 여기서 하지 않는다 — 캡쳐는 배열 위치만 쓰고 절대값은 쓰지 않으므로,
 * 값 배정을 여기 얹으면 캡쳐가 쓰지도 않는 계약을 떠안는다. 값 배정은 `overlayZIndex` 가 맡는다.
 */
import type {
  GisGeojsonLayer,
  GisTmsLayer,
  LineLayerItem,
  Project2dLayerOptions,
} from "src/domains/shared/api/gisLayer/gisLayer.model";
import {
  extractLayerName,
  extractPathSegment,
  isLayerVisible2d,
  TMS_LAYER_NAME_SEGMENT_INDEX,
} from "./utils";

/**
 * 페인팅 순서에 오른 표시 대상 레이어 한 항목.
 *
 * `kind` 는 화면 렌더 갈래(타일/gis 도면/선형 도면)다 — 캡쳐 wire 의 `kind`(`ortho`·`tms`·
 * `vector`)와는 축이 다르므로(geojson·linear 둘 다 wire 로는 `vector`) 이름을 공유하지 않는다.
 * 해석된 이름(`tmsLayerName`·`dxfJsonKey`)을 함께 담아, 소비자가 이름 규칙을 재구현하지 않게 한다.
 */
export type PaintOrderedLayer =
  | { kind: "tms"; layer: GisTmsLayer; tmsLayerName: string }
  | { kind: "geojson"; layer: GisGeojsonLayer; dxfJsonKey: string }
  | { kind: "linear"; layer: LineLayerItem; dxfJsonKey: string };

/**
 * tms 레이어의 이름 세그먼트 — 타일 URL 조립과 캡쳐 wire `layer_name` 이 같은 값을 쓴다.
 * `tiles` 경로가 없거나 세그먼트가 비면 `null` — 그 레이어는 화면에 그릴 수 없다.
 */
export function resolveTmsLayerName(layer: GisTmsLayer): string | null {
  return extractPathSegment(
    layer.tiles ?? undefined,
    TMS_LAYER_NAME_SEGMENT_INDEX
  );
}

/**
 * gis 도면(geojson) 레이어가 참조하는 S3 dxf json 키. 읽는 것은 `json` 뿐이라 옛 GIS 목록 응답의 행(`LayerPath`)도
 * 같은 키를 얻는다.
 */
export function resolveGisDxfJsonKey(
  layer: Pick<GisGeojsonLayer, "json">
): string | null {
  return extractLayerName(layer.json);
}

/**
 * 선형 레이어가 참조하는 S3 dxf json 키 — `json` 이 비어 있으면 `dxf` 파일명에서
 * `.dxf → .json` 으로 파생한다(서버가 `json` 을 채우지 않는 과거 데이터 호환).
 */
export function resolveLineDxfJsonKey(layer: LineLayerItem): string | null {
  return (
    extractLayerName(layer.json) ??
    layer.dxf?.split("/").pop()?.replace(/\.dxf$/i, ".json") ??
    null
  );
}

/**
 * 표시 대상 판정 + 아래→위 정렬.
 *
 * **입력 레이어의 부분집합을 돌려준다** — 이름의 `Visible` 이 그 탈락을 뜻한다. 숨김·렌더
 * 불능 레이어는 결과에 없으므로 호출자는 길이·index 가 `layerOptions` 와 일치한다고
 * 가정할 수 없다(원본 슬롯 수가 필요하면 `layerOptions` 에서 직접 센다).
 *
 * **판정은 화면 렌더 조건과 정확히 같아야 한다** — `show && build_complete` 만으로는
 * 부족하고, 실제로 화면에서 `null` 이 되는 조건(tms 의 `tiles`·`matrix`·`zoom_level`·이름
 * 세그먼트, 도면의 S3 데이터 존재)까지 포함한다. 화면에 안 보이는 레이어를 캡쳐 요청에
 * 넣으면 결과가 화면과 달라지고, `disjoint` 도 아니어서 서버가 걸러주지 않는다.
 *
 * 순서 규칙 (화면 실측과 동일):
 * - gis 그룹이 항상 line 그룹보다 위. 그룹 간 겹침·재배치 없음.
 * - 그룹 안에서는 입력 배열 순서를 따른다 — **index 0 이 최상단**. 입력은 패널 목록과 같은 폴더 합성
 *   평탄화 배열이다(`use2dLayerOptionsInFolderOrder`).
 * - 결과 배열은 **아래→위** — 캡쳐 계약의 index 0 = 최하단과 같은 방향이다.
 */
export function deriveVisiblePaintOrder({
  layerOptions,
  dxfData,
  lineDxfData,
}: {
  layerOptions: Project2dLayerOptions;
  /** gis 도면(dxf2geojson) S3 데이터 — 키 존재가 곧 "그릴 수 있음" */
  dxfData: Record<string, unknown>;
  /** 선형 도면(line_layer) S3 데이터 */
  lineDxfData: Record<string, unknown>;
}): PaintOrderedLayer[] {
  const gisTopToBottom = layerOptions.gis_layer.flatMap(
    (layer): PaintOrderedLayer[] => {
      if (!isLayerVisible2d(layer)) return [];

      if (layer.type === "tms_layer") {
        if (!layer.tiles || !layer.matrix || !layer.zoom_level) return [];
        const tmsLayerName = resolveTmsLayerName(layer);
        return tmsLayerName ? [{ kind: "tms", layer, tmsLayerName }] : [];
      }

      const dxfJsonKey = resolveGisDxfJsonKey(layer);
      return dxfJsonKey && dxfData[dxfJsonKey]
        ? [{ kind: "geojson", layer, dxfJsonKey }]
        : [];
    }
  );

  const lineTopToBottom = layerOptions.line_layer.flatMap(
    (layer): PaintOrderedLayer[] => {
      if (!isLayerVisible2d(layer)) return [];
      const dxfJsonKey = resolveLineDxfJsonKey(layer);
      return dxfJsonKey && lineDxfData[dxfJsonKey]
        ? [{ kind: "linear", layer, dxfJsonKey }]
        : [];
    }
  );

  // 화면 위→아래 = [...gis, ...line] 이므로 아래→위는 그 전체 역순이다.
  return [...lineTopToBottom].reverse().concat([...gisTopToBottom].reverse());
}
